import timeUtils from '@/utils/time.utils'
import { IMaintenanceEvent, IMaintenanceEventFilters, TCalendarType, TDayOfWeek } from './calendar.types'
import { TTagColor } from '@/shared/Tags/Tag'

const weekStartsOn: TDayOfWeek = 1

const decrementDates = (dates: IMaintenanceEventFilters, calendarType: TCalendarType) => {
  if (calendarType === 'month') {
    const prevMonth = timeUtils.subDays(dates.date_gte, 8)
    return getIntervalFromDate(calendarType, prevMonth)
  }
  return {
    date_gte: timeUtils.startOfWeek(timeUtils.subWeeks(dates.date_gte, 1), { weekStartsOn }),
    date_lte: timeUtils.endOfWeek(timeUtils.subWeeks(dates.date_lte, 1), { weekStartsOn }),
  }
}

const incrementDates = (dates: IMaintenanceEventFilters, calendarType: TCalendarType) => {
  if (calendarType === 'month') {
    const nextMonth = timeUtils.addDays(dates.date_lte, 8)
    return getIntervalFromDate(calendarType, nextMonth)
  }

  return {
    date_gte: timeUtils.startOfWeek(timeUtils.addWeeks(dates.date_gte, 1), { weekStartsOn }),
    date_lte: timeUtils.endOfWeek(timeUtils.addWeeks(dates.date_lte, 1), { weekStartsOn }),
  }
}

const getIntervalFromDate = (calendarType: TCalendarType, date = new Date()) => {
  switch (calendarType) {
    case 'month':
      const interval = {
        date_gte: timeUtils.startOfMonth(date),
        date_lte: timeUtils.endOfMonth(date),
      }
      interval.date_gte = timeUtils.startOfWeek(interval.date_gte, { weekStartsOn })
      interval.date_lte = timeUtils.endOfWeek(interval.date_lte, { weekStartsOn })

      const dayCount = timeUtils.differenceInCalendarDays(interval.date_lte, interval.date_gte) + 1

      // Значит необходимо прибавить (7 * 6 - dayCount) / 7 недель к правой границе промежутка,
      // если dayCount % 7 === 0, => в результате получится колчиство дней кратное 7
      if (dayCount < 7 * 6 && dayCount % 7 === 0) {
        interval.date_lte = timeUtils.addWeeks(interval.date_lte, 6 - dayCount / 7)
      }
      return interval
    case 'week':
      return {
        date_gte: timeUtils.startOfWeek(date, { weekStartsOn }),
        date_lte: timeUtils.endOfWeek(date, { weekStartsOn }),
      }
    case 'fourWeek':
      return {
        date_gte: timeUtils.startOfWeek(date, { weekStartsOn }),
        date_lte: timeUtils.endOfWeek(timeUtils.addWeeks(date, 3), { weekStartsOn }),
      }
    case 'twoWeek':
      return {
        date_gte: timeUtils.startOfWeek(date, { weekStartsOn }),
        date_lte: timeUtils.endOfWeek(timeUtils.addWeeks(date, 1), { weekStartsOn }),
      }
  }
}

const getCurrentMonth = (calendarType: TCalendarType, dates: IMaintenanceEventFilters): Date => {
  if (calendarType === 'month') return timeUtils.addDays(dates.date_gte, 8)
  if (calendarType === 'week') {
    const isCurrentWeek = timeUtils.isWithinInterval(new Date(), { start: dates.date_gte, end: dates.date_lte })
    return isCurrentWeek ? new Date() : dates.date_lte
  }
}

const getStateColor = (type: IMaintenanceEvent['type']): TTagColor => {
  switch (type) {
    case 'PLANNED_MAINTENANCE':
      return 'blue'
    case 'OVERDUE_MAINTENANCE':
      return 'red'
    case 'NO_RESPONSIBLE':
      return 'orange'
    default:
      return 'gray'
  }
}

const isIntervalInside = (inside: IMaintenanceEventFilters, outer: IMaintenanceEventFilters) => {
  return (
    (timeUtils.isAfter(inside.date_gte, outer.date_gte) || inside.date_gte.getTime() === outer.date_gte.getTime()) &&
    (timeUtils.isBefore(inside.date_lte, outer.date_lte) || inside.date_lte.getTime() === outer.date_lte.getTime())
  )
}

const getDateFromSearchParams = (params: URLSearchParams): Date | null => {
  const date = params.get('date')
  if (!date) return null

  const parsedDate = new Date(date)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const calendarUtils = {
  incrementDates,
  decrementDates,
  getIntervalFromDate,
  getCurrentMonth,
  getStateColor,
  isIntervalInside,
  getDateFromSearchParams,
}

export default calendarUtils
